import React, { ComponentProps, useState } from 'react';
import { PageLeadDefault } from '../../_lib/components/PageLead';
import { asFetchableBlock, useRefreshBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { DefaultView, EditView, Views } from '../../types/BlockWithViews';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { PageType } from '../../types/api/PageType';
import { translatePageType } from '../../_lib/components/PageType/PageTypeTitle';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { useHtmlValidId } from '../../hooks/useHtmlValidId';
import { BlockBar, BlockBarAnchor, useBlockBarCreateHref } from '../../_lib/components/BlockBar';
import { Icons } from '../../components/atoms/SVG';
import { PageMembershipMatchingRulePageLeadEditView } from './partials/PageMembershipMatchingRulePageLeadEditView';

type PageMembershipMatchingRulePageLead = Views<DefaultView<{
  areaPageType: PageType;
  roleName: string;
  description: string;
}> & EditView<any>>;

function PageMembershipMatchingRulePageLead(props: PageMembershipMatchingRulePageLead) {
  const { t } = useSonTranslation('PageMembershipMatchingRulePageLead');
  const { active } = useAppContext().editModeInfo;
  const { refresh } = useRefreshBlock();
  const id = useHtmlValidId();
  const [currentActiveView, setCurrentActiveView] = useState('default');
  const currentActiveViewHref = useBlockBarCreateHref(
    currentActiveView,
    { default: props.views.edit?.href, edit: props.views.default?.href },
    id,
  );
  const title = t('titleDefaultValue', {
    areaPageType: translatePageType(props.views.default.embedded.areaPageType as PageType),
    roleName: props.views.default.embedded.roleName,
  });

  return (
    <>
      {active
        ? <>
            <BlockBar heading={t('blockBarHeading')} hierarchy={2}>
              <BlockBarAnchor
                text={t('editTitle')}
                svgSpriteId={currentActiveView === 'edit' ? Icons.DECLINE_CROSS : Icons.EDIT_PENCIL}
                aria-controls={id}
                aria-expanded={currentActiveView === 'edit'}
                href={currentActiveViewHref}
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                  event.stopPropagation();
                  event.preventDefault();

                  if (currentActiveView === 'default') {
                    setCurrentActiveView('edit');
                  } else if (currentActiveView === 'edit') {
                    setCurrentActiveView('default');
                    refresh();
                  }
                }}
              />
            </BlockBar>
            <div id={id} className="role-page-lead">
              {currentActiveView === 'default'
                ? <PageLeadDefault
                    title={title}
                    leadText={props.views.default.embedded.description}
                  />
                : null}
              {currentActiveView === 'edit'
                ? <PageMembershipMatchingRulePageLeadEditView
                    {...props.views.edit}
                    switchToDefaultView={() => setCurrentActiveView('default')}
                  />
                : null}
            </div>
          </>
        : <PageLeadDefault
            title={title}
            leadText={props.views.default.embedded.description}
          />}
    </>
  );
}

export const PageMembershipMatchingRulePageLeadBlock =
  asFetchableBlock(PageMembershipMatchingRulePageLead, BlockType.pagemembershipmatchingrulepagelead);
export type PageMembershipMatchingRulePageLeadBlock = ComponentProps<typeof PageMembershipMatchingRulePageLeadBlock>;
